import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Typography, Table, Button, Select, Space, DatePicker, Tag, Switch, message, Input, InputNumber,
} from 'antd';
import { SearchOutlined, DownloadOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import api from '../api';
import { formatTS } from '../utils/time';
import { loadPageCache, savePageCache } from '../utils/pageCache';

import { selectFilter } from '../utils/selectFilter';
const { RangePicker } = DatePicker;

const STATUS_COLOR = {
  assigned: 'blue', in_progress: 'orange', completed: 'green',
  rescore_requested: 'purple', canceled: 'default',
};
const STATUS_LABEL = {
  assigned: 'Draft', in_progress: 'In Progress', completed: 'Completed',
  rescore_requested: 'Rescore Requested', canceled: 'Canceled',
};

const toOpts = (arr) => arr.map((v) => ({ label: v, value: v }));

export default function RawDataPage() {
  const navigate = useNavigate();

  const _cache = loadPageCache('raw-data');
  const [rows, setRows]           = useState(_cache?.rows ?? []);
  const [total, setTotal]         = useState(_cache?.total ?? 0);
  const [loading, setLoading]     = useState(false);
  const [exporting, setExporting] = useState(false);
  const [page, setPage]           = useState(_cache?.page ?? 1);
  const PAGE_SIZE = 100;

  const [filterOpts, setFilterOpts] = useState({
    campaigns: [], skills: [], agents: [], assigned_tos: [],
  });
  const [forms, setForms] = useState([]);

  const _defaultCallStart = dayjs().startOf('month').format('YYYY-MM-DD');
  const _defaultCallEnd   = dayjs().format('YYYY-MM-DD');

  const [filters, setFilters] = useState(_cache?.filters ?? {
    start_date:           _defaultCallStart,
    end_date:             _defaultCallEnd,
    eval_created_start:   '',
    eval_created_end:     '',
    campaign:             '',
    skill:                '',
    agent_user_id:        '',
    assigned_to:          '',
    form_name:            '',
    status:               '',
    include_calibration:  '',
    include_feedback_only: '',
    section_contains:     '',
    question_contains:    '',
    pts_earned_min:       '',
    pts_earned_max:       '',
  });

  // Derive a dayjs pair for the call-date RangePicker from the current filter state
  const callDatePickerValue = (filters.start_date && filters.end_date)
    ? [dayjs(filters.start_date), dayjs(filters.end_date)]
    : null;

  const committedRef = useRef(_cache?.filters ?? filters);

  useEffect(() => {
    api.get('/api/raw-data/filter-options').then((r) => setFilterOpts(r.data)).catch(() => {});
    api.get('/api/eval-forms/names')
      .then((r) => setForms(r.data.map((n) => ({ label: n, value: n }))))
      .catch(() => {});
  }, []);

  const fetchData = useCallback((committed, pg) => {
    setLoading(true);
    const params = { ...committed, page: pg, page_size: PAGE_SIZE };
    Object.keys(params).forEach((k) => { if (!params[k]) delete params[k]; });
    api.get('/api/raw-data', { params })
      .then((r) => {
        setRows(r.data.rows);
        setTotal(r.data.total);
        savePageCache('raw-data', { rows: r.data.rows, total: r.data.total, page: pg, filters: committed });
      })
      .catch(() => message.error('Failed to load raw data.'))
      .finally(() => setLoading(false));
  }, []);

  const handleSearch = () => {
    committedRef.current = filters;
    setPage(1);
    fetchData(filters, 1);
  };

  const handlePageChange = (pg) => {
    setPage(pg);
    fetchData(committedRef.current, pg);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = { ...committedRef.current, export: 1 };
      Object.keys(params).forEach((k) => { if (!params[k]) delete params[k]; });
      const res = await api.get('/api/raw-data', { params, responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `eval_raw_data_${dayjs().format('YYYY-MM-DD')}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      message.error('Export failed.');
    } finally {
      setExporting(false);
    }
  };

  const setF = (key) => (val) => setFilters((prev) => ({ ...prev, [key]: val ?? '' }));

  const columns = [
    {
      title: 'Type',
      dataIndex: 'Type',
      key: 'Type',
      width: 100,
      render: (v) => (
        <Tag color={v === 'Calibration' ? 'purple' : 'blue'}>{v}</Tag>
      ),
    },
    {
      title: 'Cal. Session',
      dataIndex: 'SessionTitle',
      key: 'SessionTitle',
      width: 160,
      ellipsis: true,
      render: (v) => v ?? '—',
    },
    {
      title: 'Eval ID',
      dataIndex: 'EvalID',
      key: 'EvalID',
      width: 75,
      render: (v) => (
        <Typography.Link onClick={() => navigate(`/cases/${v}`)}>
          {v}
        </Typography.Link>
      ),
    },
    {
      title: 'External Ref',
      dataIndex: 'ExternalRef',
      key: 'ExternalRef',
      width: 120,
      ellipsis: true,
      render: (v) => v ?? '—',
    },
    {
      title: 'Contact ID',
      dataIndex: 'ContactID',
      key: 'ContactID',
      width: 110,
      render: (v) => v ?? '—',
    },
    {
      title: 'Call Date',
      dataIndex: 'CallDate',
      key: 'CallDate',
      width: 100,
      render: (v) => v ?? '—',
    },
    {
      title: 'Campaign',
      dataIndex: 'Campaign',
      key: 'Campaign',
      width: 120,
      ellipsis: true,
      render: (v) => v ?? '—',
    },
    {
      title: 'Skill',
      dataIndex: 'Skill',
      key: 'Skill',
      width: 120,
      ellipsis: true,
      render: (v) => v ?? '—',
    },
    {
      title: 'Agent Name',
      dataIndex: 'AgentName',
      key: 'AgentName',
      width: 130,
      ellipsis: true,
      render: (v) => v ?? '—',
    },
    {
      title: 'Status',
      dataIndex: 'Status',
      key: 'Status',
      width: 130,
      render: (v) => v
        ? <Tag color={STATUS_COLOR[v] ?? 'default'}>{STATUS_LABEL[v] ?? v}</Tag>
        : '—',
    },
    {
      title: 'Acknowledged At',
      dataIndex: 'AcknowledgedAt',
      key: 'AcknowledgedAt',
      width: 160,
      render: (v) => v ? formatTS(v) : <Typography.Text type="secondary">—</Typography.Text>,
    },
    {
      title: 'Assigned To',
      dataIndex: 'AssignedTo',
      key: 'AssignedTo',
      width: 130,
      ellipsis: true,
      render: (v) => v ?? '—',
    },
    {
      title: 'Eval Created On',
      dataIndex: 'EvalCreatedAt',
      key: 'EvalCreatedAt',
      width: 150,
      render: (v) => formatTS(v),
    },
    {
      title: 'Eval Form',
      dataIndex: 'FormName',
      key: 'FormName',
      width: 160,
      ellipsis: true,
      render: (v) => v ?? '—',
    },
    {
      title: 'Version',
      dataIndex: 'FormVersion',
      key: 'FormVersion',
      width: 80,
      align: 'center',
      render: (v) => v != null ? `v${v}` : '—',
    },
    {
      title: 'Section',
      dataIndex: 'SectionName',
      key: 'SectionName',
      width: 140,
      ellipsis: true,
      render: (v) => v ?? '—',
    },
    {
      title: 'Question',
      dataIndex: 'QuestionText',
      key: 'QuestionText',
      width: 220,
      ellipsis: true,
      render: (v) => v ?? '—',
    },
    {
      title: 'Pts',
      dataIndex: 'PointsEarned',
      key: 'PointsEarned',
      width: 55,
      align: 'right',
      render: (v) => v ?? '—',
    },
    {
      title: 'Max',
      dataIndex: 'MaxPoints',
      key: 'MaxPoints',
      width: 55,
      align: 'right',
      render: (v) => v ?? '—',
    },
    {
      title: 'Response',
      dataIndex: 'Response',
      key: 'Response',
      width: 160,
      ellipsis: true,
      render: (v) => v ?? '—',
    },
    {
      title: 'Notes',
      dataIndex: 'Notes',
      key: 'Notes',
      width: 200,
      ellipsis: true,
      render: (v) => v ?? '—',
    },
  ];

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Typography.Title level={4} style={{ margin: 0 }}>Evaluations — Raw Data</Typography.Title>
        <Button
          icon={<DownloadOutlined />}
          onClick={handleExport}
          loading={exporting}
          disabled={total === 0}
        >
          Export CSV
        </Button>
      </div>

      <Space wrap style={{ marginBottom: 16 }}>
        <RangePicker
          placeholder={['Call Date From', 'Call Date To']}
          value={callDatePickerValue}
          onChange={(_, strs) =>
            setFilters((prev) => ({ ...prev, start_date: strs[0] || '', end_date: strs[1] || '' }))
          }
        />
        <RangePicker
          placeholder={['Eval Created From', 'Eval Created To']}
          onChange={(_, strs) =>
            setFilters((prev) => ({ ...prev, eval_created_start: strs[0] || '', eval_created_end: strs[1] || '' }))
          }
        />
        <Select
          placeholder="Campaign"
          style={{ width: 160 }}
          options={filterOpts.campaigns.map((c) => ({ label: c.Name, value: c.ID }))}
          value={filters.campaign || undefined}
          onChange={setF('campaign')}
          allowClear
          showSearch
          optionFilterProp="label"
        />
        <Select
          placeholder="Skill"
          style={{ width: 160 }}
          options={toOpts(filterOpts.skills)}
          value={filters.skill || undefined}
          onChange={setF('skill')}
          allowClear
          showSearch filterOption={selectFilter}
        />
        <Select
          placeholder="Agent Name"
          style={{ width: 160 }}
          options={filterOpts.agents.map((a) => ({ label: a.name, value: a.id }))}
          value={filters.agent_user_id || undefined}
          onChange={setF('agent_user_id')}
          allowClear
          showSearch
        />
        <Select
          placeholder="Assigned To"
          style={{ width: 160 }}
          options={toOpts(filterOpts.assigned_tos)}
          value={filters.assigned_to || undefined}
          onChange={setF('assigned_to')}
          allowClear
          showSearch filterOption={selectFilter}
        />
        <Select
          placeholder="Form"
          style={{ width: 180 }}
          options={forms}
          value={filters.form_name || undefined}
          onChange={setF('form_name')}
          allowClear
          showSearch
          optionFilterProp="label"
        />
        <Select
          placeholder="Status"
          style={{ width: 160 }}
          value={filters.status || undefined}
          onChange={setF('status')}
          allowClear
          options={[
            { label: 'Draft',             value: 'assigned' },
            { label: 'In Progress',       value: 'in_progress' },
            { label: 'Completed',         value: 'completed' },
            { label: 'Rescore Requested', value: 'rescore_requested' },
            { label: 'Canceled',          value: 'canceled' },
          ]}
        />
        <Space>
          <Switch
            checked={filters.include_calibration === '1'}
            onChange={(checked) => setF('include_calibration')(checked ? '1' : '')}
          />
          <span style={{ fontSize: 13 }}>Include Calibration</span>
        </Space>
        <Space>
          <Switch
            checked={filters.include_feedback_only === '1'}
            onChange={(checked) => setF('include_feedback_only')(checked ? '1' : '')}
          />
          <span style={{ fontSize: 13 }}>Include Feedback Only</span>
        </Space>
        <Input
          placeholder="Section contains…"
          style={{ width: 180 }}
          value={filters.section_contains}
          onChange={(e) => setF('section_contains')(e.target.value)}
          allowClear
        />
        <Input
          placeholder="Question contains…"
          style={{ width: 200 }}
          value={filters.question_contains}
          onChange={(e) => setF('question_contains')(e.target.value)}
          allowClear
        />
        <Space.Compact>
          <InputNumber
            placeholder="Pts min"
            style={{ width: 90 }}
            value={filters.pts_earned_min === '' ? null : Number(filters.pts_earned_min)}
            onChange={(v) => setF('pts_earned_min')(v == null ? '' : String(v))}
            min={0}
          />
          <InputNumber
            placeholder="Pts max"
            style={{ width: 90 }}
            value={filters.pts_earned_max === '' ? null : Number(filters.pts_earned_max)}
            onChange={(v) => setF('pts_earned_max')(v == null ? '' : String(v))}
            min={0}
          />
        </Space.Compact>
        <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
          Search
        </Button>
      </Space>

      <Table
        rowKey={(r, i) => `${r.EvalID}-${r.SectionOrder}-${r.QuestionOrder}-${i}`}
        dataSource={rows}
        columns={columns}
        loading={loading}
        size="small"
        scroll={{ x: 2260 }}
        pagination={{
          current: page,
          pageSize: PAGE_SIZE,
          total,
          onChange: handlePageChange,
          showTotal: (t) => `${t.toLocaleString()} rows`,
          showSizeChanger: false,
        }}
        locale={{ emptyText: 'Use the filters above and click Search to load data.' }}
      />
    </>
  );
}
